import { useState, useCallback } from "react";
import { Plus, Search, Building2 } from "lucide-react";
import { useNavigate } from "react-router";
import { useOrgContext } from "../../../context/OrgContext";
import { useLocale } from "../../../context/LanguageContext";
import {
  useAllOrganizations,
  useAdminCreateOrganization,
  useUpdateOrganization,
  useSuspendOrganization,
  useReactivateOrganization,
} from "../hooks/useOrganizations";
import { OrganizationTable } from "./OrganizationTable";
import { CreateOrganizationDialog } from "./CreateOrganizationDialog";
import { EditOrganizationDrawer } from "./EditOrganizationDrawer";
import { SuspendConfirmDialog } from "./SuspendConfirmDialog";
import { ReactivateConfirmDialog } from "./ReactivateConfirmDialog";
import type { Organization, OrganizationType, OrganizationTier, OrganizationStatus, AdminCreateOrganizationInput } from "../types";
import { ORG_TYPES, ORG_TYPE_LABEL_KEYS, ORG_TIERS, ORG_TIER_LABELS } from "../constants";
import { apolloErrorMessage } from "../../../lib/apolloError";

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: OrganizationStatus; labelKey: string }[] = [
  { value: "ACTIVE", labelKey: "organizations.status.active" },
  { value: "SUSPENDED", labelKey: "organizations.status.suspended" },
  { value: "ALL", labelKey: "organizations.page.statusAll" },
];

export function OrganizationsPage() {
  const navigate = useNavigate();
  const { setOrganization } = useOrgContext();
  const { t } = useLocale();

  // ─── State ───────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrganizationStatus>("ACTIVE");
  const [typeFilter, setTypeFilter] = useState<OrganizationType | "ALL">("ALL");
  const [tierFilter, setTierFilter] = useState<OrganizationTier | "ALL">("ALL");
  const [page, setPage] = useState(0);

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [suspendOrg, setSuspendOrg] = useState<Organization | null>(null);
  const [reactivateOrg, setReactivateOrg] = useState<Organization | null>(null);

  // ─── Queries ─────────────────────────────────────────────────────────
  const { data, loading } = useAllOrganizations({
    search: debouncedSearch || undefined,
    status: statusFilter,
    types: typeFilter !== "ALL" ? [typeFilter] : undefined,
    tiers: tierFilter !== "ALL" ? [tierFilter] : undefined,
    sortField: "CREATED_AT",
    sortOrder: "DESC",
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const organizations = data?.allOrganizations?.items ?? [];
  const total = data?.allOrganizations?.total ?? 0;

  // ─── Mutations ───────────────────────────────────────────────────────
  // `error` + `reset` are pulled per-mutation so each dialog renders its own
  // failure banner and clears it on close. Apollo auto-clears `error` on the
  // next successful call, so we only need `reset()` on the cancel path.
  const [adminCreateOrg, { loading: creating, error: createError, reset: resetCreate }] =
    useAdminCreateOrganization();
  const [updateOrg, { loading: updating, error: updateError, reset: resetUpdate }] =
    useUpdateOrganization();
  const [suspendOrgMutation, { loading: suspending, error: suspendError, reset: resetSuspend }] =
    useSuspendOrganization();
  const [reactivateOrgMutation, { loading: reactivating, error: reactivateError, reset: resetReactivate }] =
    useReactivateOrganization();

  const mutationFallback = t("common.mutationError");
  const createErrorMessage = apolloErrorMessage(createError, mutationFallback);
  const updateErrorMessage = apolloErrorMessage(updateError, mutationFallback);
  const suspendErrorMessage = apolloErrorMessage(suspendError, mutationFallback);
  const reactivateErrorMessage = apolloErrorMessage(reactivateError, mutationFallback);

  // ─── Debounced search ────────────────────────────────────────────────
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    // Simple debounce using setTimeout
    const id = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(id);
  }, []);

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleCreate = useCallback(
    async (input: AdminCreateOrganizationInput) => {
      try {
        await adminCreateOrg({ variables: { input } });
        setShowCreateDialog(false);
      } catch (err) {
        // Apollo error link handles display; log for debugging
        console.error("[OrganizationsPage] adminCreateOrganization error:", err);
      }
    },
    [adminCreateOrg],
  );

  const handleUpdate = useCallback(
    async (data: {
      name: string;
      type: OrganizationType;
      tier?: OrganizationTier;
      description?: string;
      website?: string;
    }) => {
      if (!editOrg) return;
      try {
        await updateOrg({
          variables: { id: editOrg._id, input: data },
        });
        setEditOrg(null);
      } catch (err) {
        console.error("[OrganizationsPage] updateOrganization error:", err);
      }
    },
    [updateOrg, editOrg],
  );

  const handleSuspend = useCallback(
    async (reason: string) => {
      if (!suspendOrg) return;
      try {
        await suspendOrgMutation({
          variables: {
            input: { organizationId: suspendOrg._id, reason },
          },
        });
        setSuspendOrg(null);
      } catch (err) {
        console.error("[OrganizationsPage] suspendOrganization error:", err);
      }
    },
    [suspendOrgMutation, suspendOrg],
  );

  const handleReactivate = useCallback(async () => {
    if (!reactivateOrg) return;
    try {
      await reactivateOrgMutation({
        variables: { organizationId: reactivateOrg._id },
      });
      setReactivateOrg(null);
    } catch (err) {
      console.error("[OrganizationsPage] reactivateOrganization error:", err);
    }
  }, [reactivateOrgMutation, reactivateOrg]);

  const handleDrillDown = useCallback(
    (org: Organization) => {
      // Set org context and navigate to dashboard (view as that org)
      setOrganization(org._id);
      navigate("/");
    },
    [setOrganization, navigate],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <Building2 className="h-7 w-7" />
            {t("organizations.page.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("organizations.page.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateDialog(true)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t("organizations.page.newOrganization")}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t("adminUI.organizations.searchPlaceholder")}
            className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Status filter */}
        <div className="flex rounded-md border border-border">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setStatusFilter(opt.value);
                setPage(0);
              }}
              className={`h-9 px-3 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                statusFilter === opt.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as OrganizationType | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{t("organizations.page.allTypes")}</option>
          {ORG_TYPES.map((orgType) => (
            <option key={orgType} value={orgType}>
              {t(ORG_TYPE_LABEL_KEYS[orgType])}
            </option>
          ))}
        </select>

        {/* Tier filter */}
        <select
          value={tierFilter}
          onChange={(e) => {
            setTierFilter(e.target.value as OrganizationTier | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{t("organizations.page.allTiers")}</option>
          {ORG_TIERS.map((orgTier) => (
            <option key={orgTier} value={orgTier}>
              {ORG_TIER_LABELS[orgTier]}
            </option>
          ))}
        </select>

        {/* Count badge */}
        <span className="ml-auto text-sm text-muted-foreground">
          {t("organizations.countLabel", { count: total })}
        </span>
      </div>

      {/* Table */}
      <OrganizationTable
        organizations={organizations}
        total={total}
        isLoading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onEdit={setEditOrg}
        onDrillDown={handleDrillDown}
        onSuspend={setSuspendOrg}
        onReactivate={setReactivateOrg}
      />

      {/* Dialogs */}
      {showCreateDialog && (
        <CreateOrganizationDialog
          onConfirm={handleCreate}
          onCancel={() => {
            setShowCreateDialog(false);
            resetCreate();
          }}
          loading={creating}
          error={createErrorMessage}
        />
      )}

      {editOrg && (
        <EditOrganizationDrawer
          organization={editOrg}
          onSave={handleUpdate}
          onClose={() => {
            setEditOrg(null);
            resetUpdate();
          }}
          loading={updating}
          error={updateErrorMessage}
        />
      )}

      {suspendOrg && (
        <SuspendConfirmDialog
          organization={suspendOrg}
          onConfirm={handleSuspend}
          onCancel={() => {
            setSuspendOrg(null);
            resetSuspend();
          }}
          loading={suspending}
          error={suspendErrorMessage}
        />
      )}

      {reactivateOrg && (
        <ReactivateConfirmDialog
          organization={reactivateOrg}
          onConfirm={handleReactivate}
          onCancel={() => {
            setReactivateOrg(null);
            resetReactivate();
          }}
          loading={reactivating}
          error={reactivateErrorMessage}
        />
      )}
    </div>
  );
}
